import { useEffect, useRef, useState } from 'react';
import { TimelineGroup } from '@/components/Timeline/types';
import { MedplumClient, getReferenceString } from '@medplum/core';
import { isNotFoundError } from '@/components/Timeline/types';

type useLoadMoreUntilReferenceIsFoundProps = {
  hasLoadedInitialData: boolean;
  loading: boolean;
  hasMore: boolean;
  timelineGroups: TimelineGroup[];
  scrollToRef: string | null;
  medplum: MedplumClient;
  loadMore: () => Promise<void>;
};

function getResourceIdFromReference(reference: string): string {
  const slashIndex = reference.indexOf('/');
  return slashIndex >= 0 ? reference.slice(slashIndex + 1) : reference;
}

function matchesFocusedReference(itemReference: string | undefined, focusedReference: string | null): boolean {
  if (!itemReference || !focusedReference) {
    return false;
  }

  if (itemReference === focusedReference) {
    return true;
  }

  return getResourceIdFromReference(itemReference) === getResourceIdFromReference(focusedReference);
}

export function useLoadMoreUntilReferenceIsFound({
  hasLoadedInitialData,
  loading,
  hasMore,
  timelineGroups,
  scrollToRef,
  medplum,
  loadMore,
}: useLoadMoreUntilReferenceIsFoundProps) {
  const [isWaitingForItems, setIsWaitingForItems] = useState(true);
  const [targetNotFound, setTargetNotFound] = useState(false);
  const loadMoreAttemptsRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);
  const lastTimelineSizeRef = useRef(0);

  useEffect(() => {
    loadMoreAttemptsRef.current = 0;
    loadMoreInFlightRef.current = false;
    lastTimelineSizeRef.current = 0;
    setTargetNotFound(false);
    setIsWaitingForItems(Boolean(scrollToRef));
  }, [scrollToRef]);

  useEffect(() => {
    const isForbiddenLike = (err: unknown): boolean => {
      if (!(err instanceof Error)) {
        return false;
      }
      return /forbidden|unauthorized|401|403/.test((err.message || '').toLowerCase());
    };

    const checkIfShouldWaitForItems = async () => {
      if (!scrollToRef) {
        setIsWaitingForItems(false);
        return;
      }

      const hasItemInTimeline = timelineGroups.some((timelineGroup) =>
        timelineGroup.items.some((item) => matchesFocusedReference(getReferenceString(item.reference), scrollToRef))
      );

      // Recover automatically if the item appears after we previously marked it as "not found".
      if (hasItemInTimeline) {
        if (targetNotFound) {
          setTargetNotFound(false);
        }
        if (isWaitingForItems) {
          setIsWaitingForItems(false);
        }
        return;
      }

      if (!isWaitingForItems) {
        return;
      }

      // Avoid piling up concurrent async runs while pagination is already loading.
      // The parent passes `loading` as `loading || loadingMore`.
      if (loading) {
        return;
      }

      if (!hasLoadedInitialData && !loading && timelineGroups.length > 0) {
        return;
      }

      try {
        const referenceExists = await medplum.readReference({ reference: scrollToRef });
        if (!referenceExists) {
          setIsWaitingForItems(false);
          setTargetNotFound(true);
          return;
        }
      } catch (error) {
        if (isNotFoundError(error as Error) || isForbiddenLike(error)) {
          setIsWaitingForItems(false);
          setTargetNotFound(true);
          return;
        } else {
          throw new Error(
            `Couldn't check if referenced link is valid ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      }

      // `readReference` above proved the item exists and is readable, so it is not a
      // bad link — it is simply outside this timeline's result set. Render the
      // timeline and let useAutoScroll's toast explain the missing focus, rather
      // than replacing the page with a message that blames the link (BAR-2741).
      if (hasLoadedInitialData && !hasMore && !loading) {
        setIsWaitingForItems(false);
        return;
      }

      if (loadMoreInFlightRef.current) {
        return;
      }

      // Defensive cap: prevent infinite loops when the focused resource exists
      // but is not reachable by the current timeline query (wrong patient,
      // filtered out, restricted, etc).
      //
      // Important: only start capping once we can observe that pagination isn't
      // making progress (otherwise an older-but-valid item could be incorrectly
      // flagged as missing).
      const currentSize = timelineGroups.reduce((sum, g) => sum + g.items.length, 0);
      const isStalled = currentSize <= lastTimelineSizeRef.current;
      if (isStalled && loadMoreAttemptsRef.current >= 20) {
        setIsWaitingForItems(false);
        return;
      }

      loadMoreInFlightRef.current = true;
      const beforeSize = timelineGroups.reduce((sum, g) => sum + g.items.length, 0);
      lastTimelineSizeRef.current = beforeSize;

      try {
        await loadMore();
      } finally {
        loadMoreInFlightRef.current = false;
        loadMoreAttemptsRef.current += 1;
      }
    };

    checkIfShouldWaitForItems();
  }, [
    scrollToRef,
    isWaitingForItems,
    targetNotFound,
    hasLoadedInitialData,
    loading,
    hasMore,
    timelineGroups,
    medplum,
    loadMore,
  ]);

  return { isWaitingForItems, targetNotFound };
}
